import React, { Fragment, useEffect } from 'react';
import {
  Button,
  Card,
  CardFooter,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Row,
  Table,
} from 'reactstrap';
import dayjs from 'dayjs';
import classnames from 'classnames';
import { Link } from 'react-router-dom';

import SimpleHeader from 'components/Headers/SimpleHeader.js';
import PaginationWrapper from '../components/pagination';
import useHistoryState from '../../../common/useHistoryState';
import { clickImg } from '../../../utils';
import coupangProductService from './coupangProduct.service';

function CoupangProductList({ history }) {
  const [pageActive, setPageActive] = useHistoryState(1, 'coupangProduct-pageActive');
  const [pageCount, setPageCount] = useHistoryState(1, 'coupangProduct-pageCount');
  const [data, setData] = useHistoryState([], 'coupangProduct-data');
  const [search, setSearch] = useHistoryState('', 'coupangProduct-search');
  const [searchCurrent, setSearchCurrent] = useHistoryState(false, 'coupangProduct-searchCurrent');

  const render = async () => {
    try {
      const res = await coupangProductService.list({
        page: pageActive,
        limit: 10,
        search,
      });

      if (res.code === 200) {
        setPageCount(res.list.count || 0);
        setData(res.list.rows || []);
      }
    } catch (e) {
      alert(e.response?.data?.resultMsg || '목록을 불러오지 못했습니다.');
    }
  };

  useEffect(() => {
    render();
  }, [pageActive, search]);

  const statusChange = async (id, active) => {
    try {
      const res = await coupangProductService.update(id, { active });
      if (res.code === 200) {
        render();
      }
    } catch (e) {
      alert(e.response?.data?.resultMsg || '상태 변경에 실패했습니다.');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('삭제하시겠습니까?')) {
      return;
    }

    try {
      const res = await coupangProductService.remove(id);
      if (res.code === 200) {
        render();
      }
    } catch (e) {
      alert(e.response?.data?.resultMsg || '삭제에 실패했습니다.');
    }
  };

  return (
    <>
      <SimpleHeader name="Tables" parentName="Tables" />
      <Container className="mt--6" fluid>
        <Row>
          <div className="col">
            <Card>
              <CardHeader className="border-0">
                <Row className="py-3">
                  <Col xs="11">
                    <Form>
                      <Row>
                        <Col xs="4">
                          <FormGroup>
                            <InputGroup
                              className={classnames('input-group-merge', {
                                focused: searchCurrent,
                              })}
                            >
                              <InputGroupAddon addonType="prepend">
                                <InputGroupText>
                                  <i className="fas fa-search" />
                                </InputGroupText>
                              </InputGroupAddon>
                              <Input
                                placeholder="상품명으로 검색"
                                type="text"
                                value={search}
                                onChange={(e) => {
                                  setPageActive(1);
                                  setSearch(e.target.value);
                                }}
                                onFocus={() => setSearchCurrent(true)}
                                onBlur={() => setSearchCurrent(false)}
                              />
                            </InputGroup>
                          </FormGroup>
                        </Col>
                      </Row>
                    </Form>
                  </Col>
                </Row>
                <Row>
                  <Col xs="6">
                    <h3 className="mb-0">쿠팡 상품 관리</h3>
                  </Col>
                  <Col className="text-right" xs="6">
                    <Button
                      className="btn-round btn-icon"
                      color="default"
                      onClick={() => history.push('/admin/coupangProductRegister')}
                      size="sm"
                    >
                      <span className="btn-inner--text">등록하기</span>
                    </Button>
                  </Col>
                </Row>
              </CardHeader>

              <Table className="align-items-center table-flush" responsive striped>
                <thead className="thead-light">
                  <tr>
                    <th className="text-center">등록날짜</th>
                    <th className="text-center">썸네일</th>
                    <th className="text-center">상품명</th>
                    <th className="text-center">URL</th>
                    <th className="text-center">클릭수</th>
                    <th className="text-center">활성여부</th>
                    <th className="text-center">관리</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((item, i) => {
                    return (
                      <Fragment key={item.id || i}>
                        <tr>
                          <td className="text-center">{dayjs(item.createdAt).format('YYYY-MM-DD')}</td>
                          <td className="text-center">
                            {item.thumbnail ? (
                              <img
                                alt="thumbnail"
                                style={{ width: '60px', height: '60px', borderRadius: '6px', cursor: 'pointer' }}
                                src={item.thumbnail}
                                onClick={() => clickImg(item.thumbnail)}
                              />
                            ) : (
                              '-'
                            )}
                          </td>
                          <td className="text-center">{item.name}</td>
                          <td className="text-center" style={{ maxWidth: 260, wordBreak: 'break-all' }}>
                            <a href={item.url} target="_blank" rel="noreferrer">
                              {item.url}
                            </a>
                          </td>
                          <td className="text-center">{item.clickCount || 0}</td>
                          <td className="text-center">
                            <label className="custom-toggle custom-toggle-success center">
                              <input
                                checked={!!item.active}
                                type="checkbox"
                                onChange={() => statusChange(item.id, !item.active)}
                              />
                              <span
                                className="custom-toggle-slider rounded-circle"
                                data-label-off="No"
                                data-label-on="Yes"
                              />
                            </label>
                          </td>
                          <td className="text-center">
                            <Link className="btn btn-sm btn-warning mr-2" to={`/admin/coupangProductDetail/${item.id}`}>
                              수정
                            </Link>
                            <Button color="danger" size="sm" onClick={() => remove(item.id)}>
                              삭제
                            </Button>
                          </td>
                        </tr>
                      </Fragment>
                    );
                  })}
                </tbody>
              </Table>

              <CardFooter className="py-4">
                <PaginationWrapper
                  isActive={pageActive}
                  pageNumber={pageCount}
                  callback={(page) => setPageActive(page)}
                  itemPerPage={10}
                />
              </CardFooter>
            </Card>
          </div>
        </Row>
      </Container>
    </>
  );
}

export default CoupangProductList;
